import { useSeo } from "../hooks/useSeo";
import { useRef, useState } from "react";
import { Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useRevealAll } from "../hooks/useReveal";

export default function Contact() {
  useSeo({
    title: "Kapcsolat és érdeklődés | Hair Elixir Hungary",
    description: "Kérdésed van a póthajjal vagy hajhosszabbítással kapcsolatban? Írj nekünk, segítünk kiválasztani a hajadhoz illő prémium hajat.",
  });
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    interest: "",
    length: "",
    vdetail: "",
    message: "",
  });
  const [sent, setSent] = useState(false);
  const ref = useRef(null);
  useRevealAll(ref);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    console.log("Contact inquiry:", form);
    setSent(true);
    toast.success("Köszönjük az üzenetet", {
      description: "24 órán belül válaszolunk a megadott elérhetőségen.",
    });
  };

  return (
    <main ref={ref} data-testid="page-kapcsolat" className="bg-[color:var(--he-bg)]">
      {/* === COVER HERO === */}
      <section className="relative h-[68vh] min-h-[520px] bg-[color:var(--he-ink)] text-[color:var(--he-bg)] overflow-hidden border-b border-[color:var(--he-gold)]/30">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
            alt=""
            className="w-full h-full object-cover he-ken-burns opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--he-ink)] via-[color:var(--he-ink)]/85 to-[color:var(--he-ink)]/35" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/65" />
        </div>

        <div className="absolute top-12 right-0 hidden md:block opacity-[0.06] pointer-events-none select-none">
          <span className="figure italic text-[26rem] leading-none text-[color:var(--he-gold)]">
            05
          </span>
        </div>

        <div className="relative z-10 h-full grid grid-rows-[auto_1fr]">
          <div className="pt-28 md:pt-32">
            <div className="he-container">
              <div className="flex items-center justify-between text-white/85">
                <span className="overline-xs" style={{ letterSpacing: "0.34em" }}>
                  Kapcsolat
                </span>
                <span className="hidden sm:block h-px flex-1 mx-8 bg-[color:var(--he-gold)]/40" />
                <span className="overline-xs" style={{ letterSpacing: "0.34em" }}>
                  Válasz 24 órán belül
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-end pb-16 md:pb-24">
            <div className="he-container w-full">
              <div className="max-w-4xl">
                <div className="flex items-center gap-5 mb-10 he-rise" style={{ animationDelay: "200ms" }}>
                  <span className="block w-12 h-px bg-[color:var(--he-gold)]" />
                  <span
                    className="text-[color:var(--he-gold)] uppercase text-xs"
                    style={{ letterSpacing: "0.34em", fontWeight: 500 }}
                  >
                    Írj nekünk
                  </span>
                </div>
                <h1
                  className="font-serif-display text-white tracking-[-0.022em] he-rise"
                  style={{
                    animationDelay: "350ms",
                    fontWeight: 400,
                    fontSize: "clamp(2.8rem, 7vw, 7rem)",
                    lineHeight: 1.04,
                    textShadow: "0 4px 60px rgba(0,0,0,0.6)",
                  }}
                >
                  Kérdés a termékről vagy{" "}
                  <span className="italic text-[color:var(--he-gold)]" style={{ fontWeight: 300 }}>
                    hajfelvásárlás?
                  </span>
                </h1>
                <p
                  className="mt-8 max-w-2xl text-white/85 font-light text-base md:text-lg leading-[1.7] he-rise"
                  style={{ animationDelay: "550ms", textShadow: "0 2px 20px rgba(0,0,0,0.55)" }}
                >
                  Töltsd ki az űrlapot — 24 órán belül személyesen válaszolunk.
                  Akár termékkel kapcsolatban van kérdésed, akár a saját
                  hajadat adnád el nekünk, ide írj.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-24 md:py-32 bg-[color:var(--he-bg)]">
        <div className="he-container max-w-6xl">
          {sent ? (
            <div
              data-testid="contact-confirmation"
              className="border border-[color:var(--he-gold)]/40 bg-[color:var(--he-bg-alt)] py-20 px-8 text-center max-w-2xl mx-auto he-rise"
            >
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-[color:var(--he-gold)] mx-auto">
                <Check size={20} strokeWidth={1.5} className="text-[color:var(--he-gold)]" />
              </div>
              <p className="overline text-[color:var(--he-gold)] mt-8 mb-5">Köszönjük</p>
              <h2 className="font-serif-display text-[2.6rem] md:text-[3.4rem] leading-[1.05]">
                Megkaptuk az <span className="italic text-[color:var(--he-gold)]">üzenetedet.</span>
              </h2>
              <p className="mt-7 text-[color:var(--he-ink-soft)] font-light max-w-md mx-auto leading-relaxed">
                24 órán belül válaszolunk a megadott e-mail címen vagy
                telefonon. Hétvégén kicsit tovább tarthat — köszönjük a türelmet.
              </p>
              <button
                onClick={() => {
                  setSent(false);
                  setForm({ name: "", email: "", phone: "", city: "", interest: "", length: "", vdetail: "", message: "" });
                }}
                className="mt-12 btn-ghost"
                data-testid="contact-new"
              >
                <span>Új üzenet</span>
                <ArrowRight size={14} className="ghost-arrow" />
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
              <div className="md:col-span-4 reveal">
                <p className="overline mb-6">Pár adat</p>
                <h2 className="font-serif-display text-[2rem] md:text-[2.6rem] leading-tight">
                  Csak amire <br />
                  <span className="italic text-[color:var(--he-gold)]">szükségünk van.</span>
                </h2>
                <p className="mt-6 text-sm text-[color:var(--he-ink-soft)] max-w-xs font-light leading-relaxed">
                  Az adataidat kizárólag a kapcsolatfelvételre használjuk.
                  Nem osztjuk meg harmadik féllel, és nem küldünk reklámot.
                </p>
              </div>

              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8 reveal delay-1"
              >
                <Field label="Neved" value={form.name} onChange={update("name")} required testid="contact-name" />
                <Field label="Város" value={form.city} onChange={update("city")} required testid="contact-city" />
                <Field label="E-mail" type="email" value={form.email} onChange={update("email")} required testid="contact-email" />
                <Field label="Telefon" value={form.phone} onChange={update("phone")} required testid="contact-phone" />

                <div className="sm:col-span-2 flex flex-col">
                  <label className="overline-xs text-[color:var(--he-ink-soft)] mb-3">Miben segíthetünk?</label>
                  <select
                    value={form.interest}
                    onChange={update("interest")}
                    required
                    data-testid="contact-interest"
                    className="bg-transparent border-b border-[color:var(--he-line)] py-3 text-base focus:border-[color:var(--he-gold)] outline-none transition-colors duration-300 font-light"
                  >
                    <option value="">Válassz egyet…</option>
                    <option value="rendeles">Kérdésem van egy termékről</option>
                    <option value="hajfelvasarlas">El szeretném adni a hajamat</option>
                    <option value="tanacsadas">Tanácsot kérnék a választáshoz</option>
                    <option value="egyeb">Egyéb</option>
                  </select>
                </div>

                <Field
                  label="Saját hajad hossza (felvásárláskor)"
                  value={form.length}
                  onChange={update("length")}
                  placeholder="pl. 45 cm"
                  testid="contact-length"
                />
                <Field
                  label="Vágatlan és festetlen?"
                  value={form.vdetail}
                  onChange={update("vdetail")}
                  placeholder="igen / nem / részben"
                  testid="contact-vdetail"
                />

                <div className="sm:col-span-2 flex flex-col">
                  <label className="overline-xs text-[color:var(--he-ink-soft)] mb-3">Üzenet</label>
                  <textarea
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    data-testid="contact-message"
                    className="bg-transparent border-b border-[color:var(--he-line)] py-3 text-base focus:border-[color:var(--he-gold)] outline-none resize-none transition-colors duration-300 font-light"
                    placeholder="Írd le röviden, miben segíthetünk. Felvásárlás esetén fotó linkje is jól jön."
                  />
                </div>

                <div className="sm:col-span-2 flex justify-end pt-4">
                  <button type="submit" data-testid="contact-submit" className="btn-ink">
                    <span>Üzenet küldése</span>
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <div className="flex flex-col">
      <label className="overline-xs text-[color:var(--he-ink-soft)] mb-3">
        {label}
        {required && <span className="text-[color:var(--he-gold)] ml-1">·</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        className="bg-transparent border-b border-[color:var(--he-line)] py-3 text-base focus:border-[color:var(--he-gold)] outline-none transition-colors duration-300 font-light"
      />
    </div>
  );
}
